import React, { useState, useEffect } from 'react';
import Popup from 'reactjs-popup';
import { Button, TextInput } from '@opendate-app/setlist';
import Flatpickr from 'react-flatpickr';
import maskAmount from '../../shared/utils/maskAmount';
import CrmCustomFieldsSection from '../../shared/components/CrmCustomFieldsSection';
import { canSubmitCustomFieldValue } from '../../CustomFieldValues/actions/customFieldValuesActionCreators';
import useCustomFieldValues from '../../shared/hooks/useCustomFieldValues';
import QuickCreateGivingModal from '../../shared/components/QuickCreateGivingModal';

const axios = require('axios').default;

const PAYMENT_METHODS = [
  { value: 'check', label: 'Check', icon: 'fas fa-money-check-alt' },
  { value: 'cash', label: 'Cash', icon: 'fas fa-money-bill-wave' },
  { value: 'card', label: 'Card', icon: 'fas fa-credit-card' },
  { value: 'ach', label: 'ACH / Bank', icon: 'fas fa-university' },
  { value: 'account', label: 'Account', icon: 'fas fa-wallet' },
  { value: 'in_kind', label: 'In-Kind', icon: 'fas fa-gift' },
  { value: 'stock', label: 'Stock', icon: 'fas fa-chart-line' },
  { value: 'other', label: 'Other', icon: 'fas fa-hand-holding-heart' },
];

const NEW_CAMPAIGN_SENTINEL = '__new_campaign__';
const NEW_FUND_SENTINEL = '__new_fund__';

export default function LogDonationModal({ team, contact, organization = null, csrfToken, onClose, onRecorded, customFieldDefinitions = [], initialValues = {}, dealId = null }) {
  const isOrg = !!organization;
  const fairMarketValueEnabled = !!team.can_access_fair_market_value;
  const subjectName = isOrg
    ? organization.name
    : `${contact.first_name} ${contact.last_name}`;
  const submitUrl = isOrg
    ? `/teams/${team.id}/crm/organizations/${organization.id}/donations`
    : `/teams/${team.id}/crm/fans/${contact.id}/donations`;

  const initialCampaign = initialValues.givingCampaign || null;
  const [amount, setAmount] = useState(initialValues.amount || '');
  const [donatedAt, setDonatedAt] = useState('');
  const [paymentMethod, setPaymentMethod] = useState('check');
  const [paymentReference, setPaymentReference] = useState('');
  const [givingCampaignId, setGivingCampaignId] = useState(initialCampaign ? String(initialCampaign.id) : '');
  const [appealId, setAppealId] = useState('');
  const [fundId, setFundId] = useState(initialCampaign && initialValues.fundId ? String(initialValues.fundId) : '');
  const [pledgeId, setPledgeId] = useState('');
  const [fairMarketValue, setFairMarketValue] = useState('');
  const [goodsServicesDescription, setGoodsServicesDescription] = useState('');

  const [campaigns, setCampaigns] = useState([]);
  const [funds, setFunds] = useState([]);
  const [appeals, setAppeals] = useState([]);
  const [pledges, setPledges] = useState([]);
  const [quickCreate, setQuickCreate] = useState(null);

  const handleQuickCreated = (record) => {
    if (quickCreate === 'campaign') {
      setCampaigns((prev) => [...prev, record]);
      setGivingCampaignId(String(record.id));
      setPledgeId('');
    } else {
      setFunds((prev) => [...prev, record]);
      setFundId(String(record.id));
    }
    setQuickCreate(null);
  };

  const [submitting, setSubmitting] = useState(false);
  const { customFieldValues, handleCustomFieldChange, buildCustomFieldAttributes } = useCustomFieldValues(customFieldDefinitions, []);
  const [errors, setErrors] = useState(null);

  const changeDonatedAt = (value) => {
    setDonatedAt(value);
    setErrors(null);
  };

  // Lazy-load campaigns, funds, and this donor's pledges when the modal opens.
  useEffect(() => {
    if (csrfToken) {
      axios.defaults.headers.common['X-CSRF-TOKEN'] = csrfToken;
    }

    let ignore = false;

    axios
      .get(`/teams/${team.id}/giving.json`, { params: { per_page: 100 } })
      .then((res) => {
        if (ignore) return;
        const loaded = res.data?.giving_campaigns || [];
        const missingInitial = initialCampaign && !loaded.some((c) => String(c.id) === String(initialCampaign.id));
        setCampaigns(missingInitial ? [...loaded, initialCampaign] : loaded);
      })
      .catch(() => { if (!ignore) setCampaigns(initialCampaign ? [initialCampaign] : []); });

    axios
      .get(`/teams/${team.id}/funds.json`)
      .then((res) => { if (!ignore) setFunds(Array.isArray(res.data) ? res.data : res.data?.funds || []); })
      .catch(() => { if (!ignore) setFunds([]); });

    // This donor's pledges, so a gift can be applied to one. The pledges index
    // honors top-level fan_id / organization_id params to scope to one donor.
    const pledgeParams = isOrg ? { organization_id: organization.id } : { fan_id: contact.id };
    axios
      .get(`/teams/${team.id}/crm/pledges.json`, { params: { ...pledgeParams, per_page: 100 } })
      .then((res) => { if (!ignore) setPledges(res.data?.pledges || []); })
      .catch(() => { if (!ignore) setPledges([]); });

    return () => { ignore = true; };
  }, [team.id, contact?.id, organization?.id, csrfToken, isOrg]);

  // Appeals belong to a campaign — (re)load them whenever the campaign changes.
  useEffect(() => {
    setAppealId('');
    setAppeals([]);
    if (!givingCampaignId) return;

    axios
      .get(`/teams/${team.id}/giving/${givingCampaignId}/appeals.json`)
      .then((res) => setAppeals(Array.isArray(res.data) ? res.data : res.data?.appeals || []))
      .catch(() => setAppeals([]));
  }, [team.id, givingCampaignId]);

  const numericAmount = parseFloat(amount);
  const receivedGoodsOrServices = parseFloat(fairMarketValue) > 0;
  const canSubmit = !isNaN(numericAmount) && numericAmount > 0 && paymentMethod && customFieldValues.every((cfv) => canSubmitCustomFieldValue(cfv));

  const referenceLabel = paymentMethod === 'check' ? 'Check #' : 'Reference';
  const submitLabel = canSubmit
    ? `Log $${numericAmount.toFixed(2)} Donation`
    : 'Log Donation';

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!canSubmit || submitting) return;

    setSubmitting(true);
    setErrors(null);

    try {
      const donationPayload = {
        amount: numericAmount,
        donated_at: donatedAt || null,
        payment_method: paymentMethod,
        payment_reference: paymentReference,
        giving_campaign_id: givingCampaignId || null,
        appeal_id: appealId || null,
        fund_id: fundId || null,
        pledge_id: pledgeId || null,
        custom_field_values_attributes: buildCustomFieldAttributes(),
        ...(fairMarketValueEnabled ? {
          fair_market_value: fairMarketValue || null,
          goods_services_description: receivedGoodsOrServices ? goodsServicesDescription : null,
        } : {}),
      };

      const response = await fetch(submitUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': csrfToken,
        },
        body: JSON.stringify(dealId ? { donation: donationPayload, deal_id: dealId } : { donation: donationPayload }),
      });

      const data = await response.json();

      if (!response.ok) {
        setErrors(data.errors || ['An error occurred. Please try again.']);
        setSubmitting(false);
        return;
      }

      if (onRecorded) onRecorded();
      onClose();
    } catch (err) {
      setErrors(['An unexpected error occurred. Please try again.']);
      setSubmitting(false);
    }
  };

  return (
    <Popup
      modal
      open
      onClose={onClose}
      contentStyle={{
        border: 'none',
        borderRadius: '16px',
        boxShadow: '0 20px 24px -4px rgba(10, 13, 18, 0.08), 0 8px 8px -4px rgba(10, 13, 18, 0.03)',
        padding: '0px',
        width: '480px',
        maxWidth: '90vw',
        background: 'none',
      }}
      overlayStyle={{ zIndex: '1050', backgroundColor: 'rgba(10, 13, 18, 0.5)' }}
      closeOnDocumentClick
      closeOnEscape
    >
      <div
        className="ldm-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="ldm-title"
        data-testid="log-donation-modal"
      >
        <div className="ldm-header">
          <div className="ldm-icon" aria-hidden="true">
            <i className="fas fa-hand-holding-heart" />
          </div>
          <button
            type="button"
            className="ldm-close"
            onClick={onClose}
            aria-label="Close"
            data-testid="log-donation-close"
          >
            <i className="fa fa-times" />
          </button>
          <h5 className="ldm-title" id="ldm-title">Log Donation</h5>
          <p className="ldm-subtitle">
            Record a check, cash, card, bank transfer, in-kind, stock, or other offline gift for{' '}
            <strong>{subjectName}</strong>. Nothing is charged or
            paid out.
          </p>
        </div>

        <form onSubmit={handleSubmit} data-testid="log-donation-form">
          <div className="ldm-field">
            <label className="ldm-label" htmlFor="ldm-amount">Amount *</label>
            <div className="ldm-amount-input">
              <span className="ldm-amount-prefix" aria-hidden="true">$</span>
              <input
                id="ldm-amount"
                type="text"
                inputMode="decimal"
                placeholder="0.00"
                value={amount}
                onChange={(e) => setAmount(maskAmount(e.target.value))}
                data-testid="log-donation-amount"
                autoFocus
                required
              />
            </div>
          </div>

          <div className="ldm-field">
            <label className="ldm-label" htmlFor="ldm-donated-at">Donation date (optional)</label>
            <Flatpickr
              id="ldm-donated-at"
              className="ldm-input"
              options={{ dateFormat: 'Y-m-d', disableMobile: true, static: true }}
              placeholder="Today"
              value={donatedAt}
              onChange={(_dates, dateString) => changeDonatedAt(dateString)}
              disabled={submitting}
              aria-describedby="ldm-donated-at-hint"
              data-testid="log-donation-date"
            />
            <small id="ldm-donated-at-hint" className="text-muted">
              Leave blank to use today. Dates use your account's time zone.
            </small>
            {donatedAt && (
              <Button variant="link-gray" size="sm" type="button" onClick={() => changeDonatedAt('')} disabled={submitting}>
                Use today
              </Button>
            )}
          </div>

          <div className="ldm-field">
            <span className="ldm-label">Payment Method *</span>
            <div className="ldm-method-group" role="group" aria-label="Payment method" data-testid="log-donation-payment-method">
              {PAYMENT_METHODS.map((m) => (
                <button
                  key={m.value}
                  type="button"
                  className={`ldm-method${paymentMethod === m.value ? ' is-selected' : ''}`}
                  aria-pressed={paymentMethod === m.value}
                  onClick={() => setPaymentMethod(m.value)}
                  data-testid={`log-donation-method-${m.value}`}
                >
                  <i className={m.icon} aria-hidden="true" />
                  {m.label}
                </button>
              ))}
            </div>
          </div>

          <div className="ldm-field">
            <label className="ldm-label" htmlFor="ldm-reference">{referenceLabel}</label>
            <input
              id="ldm-reference"
              type="text"
              className="ldm-input"
              value={paymentReference}
              onChange={(e) => setPaymentReference(e.target.value)}
              data-testid="log-donation-reference"
            />
          </div>

          {fairMarketValueEnabled && (
            <>
              <div className="ldm-section">
                <span className="ldm-section-label">Benefits received (optional)</span>
              </div>

              <div className="ldm-field">
                <TextInput
                  id="ldm-fmv"
                  label="Fair market value of goods or services"
                  leadingText="$"
                  inputMode="decimal"
                  placeholder="0.00"
                  hint="Leave blank if the donor received nothing in return."
                  value={fairMarketValue}
                  onChange={(e) => setFairMarketValue(maskAmount(e.target.value))}
                  disabled={submitting}
                  data-testid="log-donation-fmv"
                />
              </div>

              {receivedGoodsOrServices && (
                <div className="ldm-field">
                  <TextInput
                    id="ldm-goods-services"
                    label="Goods or services provided"
                    required
                    placeholder="e.g. Gala dinner for two"
                    maxLength={255}
                    value={goodsServicesDescription}
                    onChange={(e) => setGoodsServicesDescription(e.target.value)}
                    disabled={submitting}
                    data-testid="log-donation-goods-services"
                  />
                </div>
              )}
            </>
          )}

          <div className="ldm-section">
            <span className="ldm-section-label">Designation (optional)</span>
          </div>

          <div className="ldm-field">
            <label className="ldm-label" htmlFor="ldm-campaign">Campaign</label>
            <select
              id="ldm-campaign"
              className="ldm-input"
              value={givingCampaignId}
              // Campaign and pledge are mutually exclusive: selecting a campaign
              // clears any pledge, and the pledge select disables the reverse.
              onChange={(e) => {
                const value = e.target.value;
                if (value === NEW_CAMPAIGN_SENTINEL) {
                  setQuickCreate('campaign');
                  return;
                }
                setGivingCampaignId(value);
                if (value) setPledgeId('');
              }}
              disabled={!!pledgeId}
              data-testid="log-donation-campaign"
            >
              <option value="">{pledgeId ? '— N/A (pledge selected) —' : '— No campaign —'}</option>
              {campaigns.map((c) => (
                <option key={c.id} value={c.id}>{c.name}</option>
              ))}
              <option value={NEW_CAMPAIGN_SENTINEL}>+ Create new campaign…</option>
            </select>
          </div>

          {pledges.length > 0 && (
            <div className="ldm-field">
              <label className="ldm-label" htmlFor="ldm-pledge">Pledge</label>
              <select
                id="ldm-pledge"
                className="ldm-input"
                value={pledgeId}
                onChange={(e) => {
                  const value = e.target.value;
                  setPledgeId(value);
                  // Clear campaign designation so the gift attributes to one or the other.
                  if (value) {
                    setGivingCampaignId('');
                    setFundId('');
                  }
                }}
                disabled={!!givingCampaignId}
                data-testid="log-donation-pledge"
              >
                <option value="">{givingCampaignId ? '— N/A (campaign selected) —' : '— Not a pledge payment —'}</option>
                {pledges.map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.display_number} — {p.name} ($
                    {((p.balance_remaining_cents || 0) / 100).toFixed(2)} remaining)
                  </option>
                ))}
              </select>
            </div>
          )}

          {givingCampaignId && (
            <>
              <div className="ldm-field">
                <label className="ldm-label" htmlFor="ldm-appeal">Appeal</label>
                <select
                  id="ldm-appeal"
                  className="ldm-input"
                  value={appealId}
                  onChange={(e) => setAppealId(e.target.value)}
                  data-testid="log-donation-appeal"
                >
                  <option value="">— None —</option>
                  {appeals.map((a) => (
                    <option key={a.id} value={a.id}>{a.name}</option>
                  ))}
                </select>
              </div>

              <div className="ldm-field">
                <label className="ldm-label" htmlFor="ldm-fund">Fund</label>
                <select
                  id="ldm-fund"
                  className="ldm-input"
                  value={fundId}
                  onChange={(e) => {
                    if (e.target.value === NEW_FUND_SENTINEL) {
                      setQuickCreate('fund');
                      return;
                    }
                    setFundId(e.target.value);
                  }}
                  data-testid="log-donation-fund"
                >
                  <option value="">— None —</option>
                  {funds.map((f) => (
                    <option key={f.id} value={f.id}>{f.name}</option>
                  ))}
                  <option value={NEW_FUND_SENTINEL}>+ Create new fund…</option>
                </select>
              </div>
            </>
          )}

          {quickCreate && (
            <QuickCreateGivingModal
              kind={quickCreate}
              teamId={team.id}
              onCreated={handleQuickCreated}
              onClose={() => setQuickCreate(null)}
            />
          )}

          <CrmCustomFieldsSection
            team={team}
            customFieldDefinitions={customFieldDefinitions}
            customFieldValues={customFieldValues}
            onCustomFieldChange={handleCustomFieldChange}
            defaultExpanded
          />

          {errors && (
            <div className="ldm-error" role="alert" data-testid="log-donation-errors">
              {Array.isArray(errors)
                ? errors.map((err, i) => <div key={i}>{err}</div>)
                : <div>{errors}</div>}
            </div>
          )}

          <div className="ldm-footer">
            <button
              type="button"
              className="btn-setlist-secondary"
              onClick={onClose}
              disabled={submitting}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="btn-setlist-primary"
              disabled={!canSubmit || submitting}
              data-testid="log-donation-submit"
            >
              {submitting ? 'Saving…' : submitLabel}
            </button>
          </div>
        </form>
      </div>
    </Popup>
  );
}
